import { useState, useEffect, useMemo, useCallback } from 'react';
import { usePromptStore } from '../../../store/promptManager';
import TemplateSelector from './TemplateSelector';
import CustomPromptEditor from './CustomPromptEditor';

interface PromptSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
}

const PromptSettingsPanel = ({ isOpen, onClose, documentId }: PromptSettingsPanelProps) => {
  const [activeTab, setActiveTab] = useState<'function'|'scenario'|'custom'>('function');
  
  // 使用更简单的state订阅方式
  const store = usePromptStore();
  
  // 从store中获取需要的状态和方法
  const { 
    templates,
    defaultFunctionTemplateId,
    defaultScenarioTemplateId,
    documentSettings,
    getFinalPromptForDocument
  } = store;
  
  // 调试 - 检查模板是否正确加载
  useEffect(() => {
    console.log("Templates loaded:", templates);
    console.log("Default function template ID:", defaultFunctionTemplateId);
    console.log("Default scenario template ID:", defaultScenarioTemplateId);
  }, [templates, defaultFunctionTemplateId, defaultScenarioTemplateId]);
  
  // 获取当前文档的设置
  const docSettings = useMemo(() => 
    documentSettings[documentId] || {}, 
    [documentSettings, documentId]
  );
  
  // 当前选中的模板ID
  const functionTemplateId = useMemo(() => 
    docSettings.functionTemplateId || defaultFunctionTemplateId, 
    [docSettings, defaultFunctionTemplateId]
  );
  
  const scenarioTemplateId = useMemo(() => 
    docSettings.scenarioTemplateId || defaultScenarioTemplateId, 
    [docSettings, defaultScenarioTemplateId]
  );
  
  // 处理函数
  const handleResetToDefaults = useCallback(() => {
    store.clearDocumentSettings(documentId);
  }, [store, documentId]);
  
  // 使用 useMemo 缓存最终提示词结果，避免每次渲染都重新计算
  const finalPrompt = useMemo(() => {
    return getFinalPromptForDocument(documentId);
  }, [getFinalPromptForDocument, documentId]);
  
  // 使用 useMemo 对模板列表进行过滤，避免每次渲染都重新计算
  const functionTemplates = useMemo(() => {
    const templates_array = Object.values(templates || {});
    console.log("Function templates before filter:", templates_array.length);
    const filtered = templates_array.filter(t => t && t.category === 'function');
    console.log("Function templates after filter:", filtered.length);
    return filtered;
  }, [templates]);
  
  const scenarioTemplates = useMemo(() => {
    const templates_array = Object.values(templates || {});
    console.log("Scenario templates before filter:", templates_array.length);
    const filtered = templates_array.filter(t => t && t.category === 'scenario');
    console.log("Scenario templates after filter:", filtered.length);
    return filtered;
  }, [templates]);
  
  // 缓存模板选择处理函数
  const handleFunctionTemplateSelect = useCallback((id: string) => {
    store.setDocumentTemplates(documentId, id, undefined);
  }, [store, documentId]);
  
  const handleScenarioTemplateSelect = useCallback((id: string) => {
    store.setDocumentTemplates(documentId, undefined, id);
  }, [store, documentId]);
  
  const handleCustomPromptSave = useCallback((prompt: string) => {
    store.setDocumentCustomPrompt(documentId, prompt);
  }, [store, documentId]);
  
  return (
    <div className={`prompt-settings-panel ${isOpen ? 'open' : ''}`}>
      <div className="panel-header">
        <h3>AI 提示词设置</h3>
        <button className="editor-button secondary" onClick={onClose}>关闭</button>
      </div>
      
      <div className="panel-tabs">
        <button 
          className={activeTab === 'function' ? 'active' : ''} 
          onClick={() => setActiveTab('function')}
        >
          功能模板
        </button>
        <button 
          className={activeTab === 'scenario' ? 'active' : ''} 
          onClick={() => setActiveTab('scenario')}
        >
          情景模板
        </button>
        <button 
          className={activeTab === 'custom' ? 'active' : ''} 
          onClick={() => setActiveTab('custom')}
        >
          自定义提示词
        </button>
      </div>
      
      <div className="panel-content">
        {activeTab === 'function' && (
          <>
            {functionTemplates.length === 0 ? (
              <div className="no-templates-message">
                <p>未找到功能模板。请检查配置文件或添加新模板。</p>
              </div>
            ) : (
              <TemplateSelector 
                templates={functionTemplates}
                selectedTemplateId={functionTemplateId}
                onSelect={handleFunctionTemplateSelect}
              />
            )}
          </>
        )}
        
        {activeTab === 'scenario' && (
          <>
            {scenarioTemplates.length === 0 ? (
              <div className="no-templates-message">
                <p>未找到情景模板。请检查配置文件或添加新模板。</p>
              </div>
            ) : (
              <TemplateSelector 
                templates={scenarioTemplates}
                selectedTemplateId={scenarioTemplateId}
                onSelect={handleScenarioTemplateSelect}
              />
            )}
          </>
        )}
        
        {activeTab === 'custom' && (
          <CustomPromptEditor 
            documentId={documentId}
            initialValue={docSettings.customPrompt || ''}
            onSave={handleCustomPromptSave}
          />
        )}
      </div>
      
      <div className="panel-footer">
        <button 
          className="editor-button secondary"
          onClick={handleResetToDefaults}
        >
          重置为默认值
        </button>
        
        <div className="preview-prompt">
          {/* 显示最终组合的提示词预览 */}
          <details>
            <summary>查看最终提示词</summary>
            <div className="prompt-preview">
              <h4>系统提示词:</h4>
              <pre>{finalPrompt.systemPrompt}</pre>
              
              <h4>用户提示词:</h4>
              <pre>{finalPrompt.userPrompt || '<无>'}</pre>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
};

export default PromptSettingsPanel; 